import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Heading, Text, Stack, HStack, Badge, Icon, Spinner } from "@chakra-ui/react";
import { MapPin, Users, AlertTriangle, Link2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { colors } from "@/theme/colors";

const fmt = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

function CrewMember({ m, navigate }) {
  const overdue = m.overdue || [];
  const actions = m.open_actions || [];
  return (
    <Box borderTop="1px solid" borderColor={colors.border} pt="3" data-testid={`crew-member-${m.worker_id}`}>
      <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap">
        <Box>
          <Text fontWeight="600" fontSize="sm" color={colors.textPrimary}>{m.full_name}</Text>
          <Text fontSize="xs" color={colors.textSecondary}>
            {m.job_role || "Crew"} · Last submission: {fmt(m.last_at)} · {m.subs_7d} in the last 7 days
          </Text>
        </Box>
        {overdue.length === 0 ? (
          <Badge bg={colors.resolvedBg} color={colors.resolvedText} borderRadius="full" px="2.5" data-testid={`crew-ok-${m.worker_id}`}>Up to date</Badge>
        ) : (
          <Badge bg={colors.criticalBg} color={colors.criticalText} borderRadius="full" px="2.5" data-testid={`crew-overdue-${m.worker_id}`}>
            {overdue.length} overdue
          </Badge>
        )}
      </Flex>

      {overdue.length > 0 && (
        <Stack gap="1" mt="2">
          {overdue.map((o) => (
            <HStack key={o.assignment_id} gap="2" px="3" py="1.5" borderRadius="8px" bg={colors.criticalBg} data-testid={`overdue-${m.worker_id}-${o.assignment_id}`}>
              <Icon as={AlertTriangle} boxSize="3.5" color={colors.poppy} flexShrink={0} />
              <Text fontSize="xs" color={colors.criticalText}>
                <b>{o.title}</b> — due {o.due_date ? fmt(o.due_date + "T00:00:00") : "—"}{o.location ? ` · ${o.location}` : ""}
              </Text>
            </HStack>
          ))}
        </Stack>
      )}

      {actions.length > 0 && (
        <Stack gap="1" mt="2">
          {actions.map((a) => (
            <Flex key={a.id} as="button" type="button" onClick={() => navigate(`/submissions/${a.id}`)}
              data-testid={`open-action-${a.id}`} w="full" textAlign="left" align="center" gap="2"
              px="3" py="1.5" borderRadius="8px" bg={colors.poppyTint} _hover={{ opacity: 0.85 }}>
              <Icon as={Link2} boxSize="3.5" color={colors.poppy} flexShrink={0} />
              <Text fontSize="xs" color={colors.poppy}><b>{a.type}</b> open since {fmt(a.created_at)}</Text>
            </Flex>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export default function MyCrew() {
  const navigate = useNavigate();
  const { user, activeCompanyId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!activeCompanyId) { setLoading(false); return; }
    safeQuery(supabase.rpc("my_crew_data", { p_company: activeCompanyId })).then(({ data: d, error }) => {
      if (error) {
        // Offline: serve the last-known-good crew board instead of an error.
        const snap = cacheGet(user.id, "mycrew");
        if (snap) setData(snap);
        else setErr(navigator.onLine ? error.message
          : "You're offline — this screen hasn't been loaded on this device yet. It will appear after your first online visit.");
      } else {
        setData(d);
        cachePut(user.id, "mycrew", d);
      }
      setLoading(false);
    });
  }, [activeCompanyId, user.id]);

  if (loading) return <Flex minH="40vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;

  const sites = data?.sites || [];

  return (
    <Box data-testid="mycrew-page" maxW="860px">
      <Heading fontFamily="heading" fontWeight="700" fontSize={{ base: "2xl", md: "3xl" }} color={colors.textPrimary}>My Sites & Crew</Heading>
      <Text color={colors.textSecondary} mb="5" fontSize={{ base: "sm", md: "md" }}>
        Read-only view of your assigned job sites, the crew at each, overdue scheduled forms and open action items.
      </Text>

      {err && <Box mb="4" px="4" py="3" borderRadius="10px" bg={colors.criticalBg} color={colors.criticalText} fontSize="sm" data-testid="mycrew-error">{err}</Box>}

      {sites.length === 0 ? (
        <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="6" data-testid="mycrew-empty">
          <HStack gap="2" mb="1"><Icon as={MapPin} boxSize="4" color={colors.teal} /><Text fontWeight="600" color={colors.textPrimary}>No sites assigned yet</Text></HStack>
          <Text fontSize="sm" color={colors.textSecondary}>Ask your Org Admin to assign you to one or more job sites (Locations & Employees → your employee record).</Text>
        </Box>
      ) : (
        <Stack gap="4">
          {sites.map((s) => (
            <Box key={s.id} bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" data-testid={`site-card-${s.id}`}>
              <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap" mb="1">
                <HStack gap="2">
                  <Icon as={MapPin} boxSize="4" color={colors.teal} />
                  <Text fontWeight="700" color={colors.textPrimary}>{s.name}</Text>
                </HStack>
                <HStack gap="1.5" color={colors.textSecondary}>
                  <Icon as={Users} boxSize="3.5" />
                  <Text fontSize="xs">{(s.crew || []).length} crew</Text>
                </HStack>
              </Flex>
              {s.address && <Text fontSize="xs" color={colors.textSecondary} mb="2">{s.address}</Text>}

              {(s.crew || []).length === 0 ? (
                <Text fontSize="sm" color={colors.textSecondary}>No crew assigned to this site yet.</Text>
              ) : (
                <Stack gap="3">
                  {s.crew.map((m) => <CrewMember key={m.worker_id} m={m} navigate={navigate} />)}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
